// İSG uzmanı portalı — Risk detay: tehlike kalemleri paneli (api-contract §19.3/§19.4). Her
// kalem kartı: tehlike + risk/etkilenenler/mevcut önlem/alınacak önlem + **sunucudan gelen**
// score/level rozeti (Olasılık×Şiddet) + **artık risk** rozeti + **DÖF listesi** (ekle/düzenle/
// sil). Kalem ekle/düzenle/sil yalnız **düzenlenebilir** değerlendirmede (aksi salt-okunur;
// BE `409 RISK_ASSESSMENT_NOT_EDITABLE` UI'da önlenir).
//
// **⛔ score/level rozeti SUNUCUDAN gelen `item.level`/`item.score` (§19).** İstemci yeniden
// hesaplamaz — matris seçici canlı önizlemesi yalnız yazma formundaydı; burada kanonik değer gösterilir.

import {
  Button,
  ConfirmDialog,
  EmptyState,
  type RiskActionDto,
  type RiskAssessmentDto,
  type RiskItemDto,
  Tag,
  currentCulture,
  riskApi,
} from '@isgportal/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Pencil, Plus, Trash2, UserRound } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../lib/format';
import { riskKeys } from '../../lib/riskKeys';
import { RiskActionFormModal } from './RiskActionFormModal';
import { RiskItemFormModal } from './RiskItemFormModal';
import { RiskLevelBadge } from './RiskLevelBadge';
import { isRiskActionOverdue, riskActionStatusTone } from './riskEnums';

interface RiskItemsPanelProps {
  assessment: RiskAssessmentDto;
  items: RiskItemDto[];
  editable: boolean;
}

/** Aktif DÖF modalı hedefi (hangi kalem + düzenlenen DÖF). */
interface ActionTarget {
  itemUuid: string;
  action: RiskActionDto | null;
}

export function RiskItemsPanel({ assessment, items, editable }: RiskItemsPanelProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const assessmentUuid = assessment.uuid;

  const [itemModal, setItemModal] = useState<{ open: boolean; item: RiskItemDto | null }>({
    open: false,
    item: null,
  });
  const [itemDelete, setItemDelete] = useState<RiskItemDto | null>(null);
  const [actionModal, setActionModal] = useState<ActionTarget | null>(null);
  const [actionDelete, setActionDelete] = useState<{
    itemUuid: string;
    action: RiskActionDto;
  } | null>(null);

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: riskKeys.detail(assessmentUuid) });
    queryClient.invalidateQueries({ queryKey: riskKeys.list() });
  }

  const deleteItemMutation = useMutation({
    mutationFn: (itemUuid: string) => riskApi.deleteRiskItem(assessmentUuid, itemUuid),
    onSuccess: () => {
      invalidate();
      setItemDelete(null);
    },
  });
  const deleteActionMutation = useMutation({
    mutationFn: (target: { itemUuid: string; actionUuid: string }) =>
      riskApi.deleteRiskAction(assessmentUuid, target.itemUuid, target.actionUuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: riskKeys.detail(assessmentUuid) });
      setActionDelete(null);
    },
  });

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold text-fg">{t('risk.detail.items.title')}</h2>
        {editable ? (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setItemModal({ open: true, item: null })}
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            {t('risk.detail.items.add')}
          </Button>
        ) : null}
      </div>

      {items.length === 0 ? (
        <EmptyState variant="panel" title={t('risk.detail.items.empty')} />
      ) : (
        <ol className="flex flex-col gap-4">
          {items.map((item) => (
            <RiskItemCard
              key={item.uuid}
              item={item}
              editable={editable}
              onEdit={() => setItemModal({ open: true, item })}
              onDelete={() => setItemDelete(item)}
              onAddAction={() => setActionModal({ itemUuid: item.uuid, action: null })}
              onEditAction={(action) => setActionModal({ itemUuid: item.uuid, action })}
              onDeleteAction={(action) => setActionDelete({ itemUuid: item.uuid, action })}
            />
          ))}
        </ol>
      )}

      {/* Kalem ekle/düzenle */}
      {itemModal.open ? (
        <RiskItemFormModal
          open={itemModal.open}
          onOpenChange={(open) => setItemModal((prev) => ({ ...prev, open }))}
          assessmentUuid={assessmentUuid}
          item={itemModal.item}
        />
      ) : null}

      {/* DÖF ekle/düzenle */}
      {actionModal ? (
        <RiskActionFormModal
          open={actionModal !== null}
          onOpenChange={(open) => !open && setActionModal(null)}
          assessmentUuid={assessmentUuid}
          itemUuid={actionModal.itemUuid}
          action={actionModal.action}
        />
      ) : null}

      {/* Kalem sil */}
      <ConfirmDialog
        open={itemDelete !== null}
        onOpenChange={(open) => !open && setItemDelete(null)}
        title={t('risk.detail.items.delete.title')}
        body={t('risk.detail.items.delete.body', { hazard: itemDelete?.hazard ?? '' })}
        confirmLabel={t('risk.detail.items.delete.confirm')}
        confirmVariant="danger"
        loading={deleteItemMutation.isPending}
        error={deleteItemMutation.isError ? deleteItemMutation.error : undefined}
        onConfirm={() => itemDelete && deleteItemMutation.mutate(itemDelete.uuid)}
      />

      {/* DÖF sil */}
      <ConfirmDialog
        open={actionDelete !== null}
        onOpenChange={(open) => !open && setActionDelete(null)}
        title={t('risk.detail.action.delete.title')}
        body={t('risk.detail.action.delete.body', {
          description: actionDelete?.action.description ?? '',
        })}
        confirmLabel={t('risk.detail.action.delete.confirm')}
        confirmVariant="danger"
        loading={deleteActionMutation.isPending}
        error={deleteActionMutation.isError ? deleteActionMutation.error : undefined}
        onConfirm={() =>
          actionDelete &&
          deleteActionMutation.mutate({
            itemUuid: actionDelete.itemUuid,
            actionUuid: actionDelete.action.uuid,
          })
        }
      />
    </section>
  );
}

interface RiskItemCardProps {
  item: RiskItemDto;
  editable: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onAddAction: () => void;
  onEditAction: (action: RiskActionDto) => void;
  onDeleteAction: (action: RiskActionDto) => void;
}

/** Etiket + değer satırı (yalnız değer varsa). */
function ItemFact({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value || value.length === 0) {
    return null;
  }
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs font-medium text-muted">{label}</dt>
      <dd className="text-sm text-fg">{value}</dd>
    </div>
  );
}

function RiskItemCard({
  item,
  editable,
  onEdit,
  onDelete,
  onAddAction,
  onEditAction,
  onDeleteAction,
}: RiskItemCardProps) {
  const { t } = useTranslation();
  const culture = currentCulture();

  return (
    <li className="flex flex-col gap-3 rounded-lg border border-border bg-surface p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
            {item.order}
          </span>
          <div className="flex flex-col gap-1.5">
            <span className="font-medium text-fg">{item.hazard}</span>
            <div className="flex flex-wrap items-center gap-2">
              {/* Sunucudan gelen band (§19) */}
              <RiskLevelBadge level={item.level} score={item.score} />
              <span className="text-xs text-muted">
                {t('risk.item.scoreDisplay', {
                  probability: item.probability,
                  severity: item.severity,
                })}
              </span>
              {item.residualLevel && item.residualScore != null ? (
                <span className="flex items-center gap-1 text-xs text-muted">
                  {t('risk.item.residualLabel')}
                  <RiskLevelBadge level={item.residualLevel} score={item.residualScore} />
                </span>
              ) : null}
            </div>
          </div>
        </div>
        {editable ? (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={onEdit}
              aria-label={t('risk.detail.items.edit')}
            >
              <Pencil className="h-4 w-4" aria-hidden="true" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onDelete}
              aria-label={t('risk.detail.items.remove')}
            >
              <Trash2 className="h-4 w-4 text-danger" aria-hidden="true" />
            </Button>
          </div>
        ) : null}
      </div>

      <dl className="grid grid-cols-1 gap-3 pl-9 sm:grid-cols-2">
        <ItemFact label={t('risk.item.field.risk')} value={item.risk} />
        <ItemFact label={t('risk.item.field.affectedPersons')} value={item.affectedPersons} />
        <ItemFact label={t('risk.item.field.existingControls')} value={item.existingControls} />
        <ItemFact
          label={t('risk.item.field.recommendedControls')}
          value={item.recommendedControls}
        />
      </dl>

      {/* DÖF */}
      <div className="ml-9 flex flex-col gap-2 rounded-md border border-border bg-surface-muted/40 p-3">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-semibold text-muted">{t('risk.detail.action.title')}</span>
          {editable ? (
            <Button variant="ghost" size="sm" onClick={onAddAction}>
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
              {t('risk.detail.action.add')}
            </Button>
          ) : null}
        </div>
        {item.actions.length === 0 ? (
          <p className="text-xs text-muted">{t('risk.detail.action.empty')}</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {item.actions.map((action) => (
              <li
                key={action.uuid}
                className="flex flex-wrap items-start justify-between gap-2 rounded-md bg-surface px-3 py-2"
              >
                <div className="flex flex-col gap-1">
                  <span className="text-sm text-fg">{action.description}</span>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted">
                    <Tag color={riskActionStatusTone(action.status)}>
                      {t(`risk.action.status.${action.status}`)}
                    </Tag>
                    {action.responsibleName ? (
                      <span className="inline-flex items-center gap-1">
                        <UserRound className="h-3.5 w-3.5" aria-hidden="true" />
                        {action.responsibleName}
                      </span>
                    ) : null}
                    {action.dueDate ? (
                      <span className="inline-flex items-center gap-1">
                        <CalendarClock className="h-3.5 w-3.5" aria-hidden="true" />
                        {formatDate(action.dueDate, culture)}
                      </span>
                    ) : null}
                    {isRiskActionOverdue(action) ? (
                      <Tag color="red">{t('risk.detail.action.overdue')}</Tag>
                    ) : null}
                  </div>
                  {action.note ? <span className="text-xs text-muted">{action.note}</span> : null}
                </div>
                {editable ? (
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEditAction(action)}
                      aria-label={t('risk.detail.action.edit')}
                    >
                      <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDeleteAction(action)}
                      aria-label={t('risk.detail.action.remove')}
                    >
                      <Trash2 className="h-3.5 w-3.5 text-danger" aria-hidden="true" />
                    </Button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </li>
  );
}
